import { useState, type ReactNode } from 'react';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { makeQueryClient, tsr } from './lib/api.js';
import { SessionProvider, useSession, type Session } from './lib/session.js';
import { I18nProvider } from './lib/i18n.js';
import { Login } from './features/Login.js';
import { AppRoutes } from './routes/AppRoutes.js';
import './app.css';

/**
 * The session is the single auth switch: no session → the login view (no router
 * needed); a session → the client-side router with the full route tree, so every
 * authenticated page has a real URL and browser history works.
 *
 * The router is injectable so tests can seed an initial URL via `MemoryRouter`
 * (`initialEntries`); production uses `BrowserRouter` against the real history.
 */
function Routed({ router }: { router: (children: ReactNode) => ReactNode }) {
  const { isAuthenticated } = useSession();
  return isAuthenticated ? router(<AppRoutes />) : <Login />;
}

export interface AppProps {
  /**
   * Seed the session for tests / storybook. Production leaves this undefined so
   * the session hydrates from `localStorage`.
   */
  initialSession?: Session | null;
  /**
   * Seed the initial route(s) for tests. When provided, the app uses an in-memory
   * router starting at these entries; omitted in production (real browser history).
   */
  initialEntries?: string[];
}

/**
 * The web app root (Req 6.5): a TanStack Query client, the ts-rest react-query
 * provider (so hooks resolve against the typed client), the i18n/direction
 * provider (English + Arabic/RTL), the session provider that gates the UI, and —
 * once authenticated — the selected-client context that drives the two-level
 * admin/client navigation. Styling is the teal-glass design system in `app.css`
 * + `styles/` tokens; see `.impeccable/surfaces/` for the direction contract.
 */
export function App({ initialSession, initialEntries }: AppProps) {
  // One QueryClient per App instance (tests get a fresh, retry-disabled client).
  const [queryClient] = useState(() => makeQueryClient());

  const router = (children: ReactNode) =>
    initialEntries ? (
      <MemoryRouter initialEntries={initialEntries}>{children}</MemoryRouter>
    ) : (
      // basename tracks Vite's `base` (/app/ in prod, served behind the
      // clarity-ai.rest/app edge route) so client-side routing resolves under
      // the subpath. Trailing slash stripped — react-router wants `/app`, not
      // `/app/`. BASE_URL is `/` when base is unset, which yields no basename.
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
        {children}
      </BrowserRouter>
    );

  return (
    <QueryClientProvider client={queryClient}>
      <tsr.ReactQueryProvider>
        <I18nProvider>
          <SessionProvider initialSession={initialSession}>
            <Routed router={router} />
          </SessionProvider>
        </I18nProvider>
      </tsr.ReactQueryProvider>
    </QueryClientProvider>
  );
}
